import React from 'react';
import { Descriptions, Empty, Table, Typography } from 'antd';
import { Pie, Column } from '@ant-design/plots';
import ReportSection, { ReportSubTitle } from './components/ReportSection';
import { formatPercent, renderPlainValue, toHitMissTag } from './trans.effect';
import { toBuerXingyaoViewModel } from './struct';

interface Props {
  data: any;
}

/**
 * 将数值安全转换为 number；无法转换时返回 fallback
 */
const toNumber = (value: any, fallback = 0) => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : fallback;
};

/**
 * 生成星耀分区间条的数据（从极低风险到极高风险）
 */
const getScoreSegments = () => {
  return [
    { min: 570, max: 650, label: '极低风险', color: '#52c41a' },
    { min: 530, max: 570, label: '低风险', color: '#36cfc9' },
    { min: 470, max: 530, label: '中风险', color: '#1890ff' },
    { min: 430, max: 470, label: '高风险', color: '#faad14' },
    { min: 0, max: 430, label: '极高风险', color: '#f5222d' },
  ];
};

/**
 * 基于星耀分定位落点与激活区间（等距区间展示）
 */
const getScorePosition = (score: number | null) => {
  const segs = getScoreSegments();
  if (score === null) return { leftPct: null, activeIdx: -1, activeColor: '#d9d9d9' };
  const min = 0;
  const max = 650;
  const clamped = Math.max(min, Math.min(max, score));

  const activeIdx = segs.findIndex((s) => {
    if (s.label === '极低风险') return clamped > 570 && clamped <= 650;
    if (s.label === '低风险') return clamped > 530 && clamped <= 570;
    if (s.label === '中风险') return clamped > 470 && clamped <= 530;
    if (s.label === '高风险') return clamped > 430 && clamped <= 470;
    return clamped >= 0 && clamped <= 430;
  });

  const segWidthPct = 100 / segs.length;
  const safeIdx = activeIdx >= 0 ? activeIdx : segs.length - 1;
  const seg = segs[safeIdx];
  const segStartScore = seg.label === '极低风险' ? 650 : seg.label === '低风险' ? 570 : seg.label === '中风险' ? 530 : seg.label === '高风险' ? 470 : 430;
  const segEndScore = seg.label === '极低风险' ? 570 : seg.label === '低风险' ? 530 : seg.label === '中风险' ? 470 : seg.label === '高风险' ? 430 : 0;
  const within = segStartScore === segEndScore ? 0 : (segStartScore - clamped) / (segStartScore - segEndScore);
  const leftPct = safeIdx * segWidthPct + Math.max(0, Math.min(1, within)) * segWidthPct;

  return { leftPct, activeIdx: safeIdx, activeColor: seg.color };
};

const BuerXingyaoTab: React.FC<Props> = ({ data }) => {
  if (!data) return <Empty description="暂无数据" />;

  const vm = toBuerXingyaoViewModel(data);
  if (!vm) return <Empty description="数据解析失败" />;

  const riskListLabelMap: Record<string, string> = {
    census_register_high_risk_area: '归属地位于高风险集中地区',
    idcard_hit_clzlwy: '车辆租赁违约名单',
    idcard_hit_fztj: '犯罪通缉名单',
    idcard_hit_gywzcc: '故意违章乘车名单',
    phone_hit_txxh: '通信小号库',
    phone_hit_xjhm: '虚拟号码库',
    result_xd: '特殊关注名单',
    credit_rent: '信用租赁风险名单',
  };

  const riskListKeysOrdered = [
    'credit_rent',
    'result_xd',
    'idcard_hit_fztj',
    'phone_hit_xjhm',
    'phone_hit_txxh',
    'census_register_high_risk_area',
    'idcard_hit_clzlwy',
    'idcard_hit_gywzcc',
  ];
  const isHit = (v: any) => {
    const s = String(v ?? '').trim().toLowerCase();
    return s === '1' || s === 'true' || s === '是';
  };

  const dataTypeMap: Record<string, string> = {
    cpws: '裁判文书',
    ktgg: '开庭公告',
    ajlc: '案件流程信息',
    fygg: '法院公告',
    shixin: '失信公告',
    zxgg: '执行公告',
    bgt: '曝光台',
  };

  const identMap: Record<string, string> = { '01': '不一致', '02': '一致' };
  const timeOnlineMap: Record<string, string> = {
    '1': '[0,3）',
    '2': '[3,6）',
    '3': '[6,12）',
    '4': '[12,24）',
    '5': '[24,+)',
    '0': '查无此号或已注销',
    '-1': '不支持该运营商',
    '-999': '手机状态异常',
  };

  const demandRowDefs = [
    { key: 'apply_setup_time', label: '申请查询总数' },
    { key: 'apply_setup_time_ybxffq', label: '一般消费分期平台' },
    { key: 'apply_setup_time_xyk', label: '信用卡' },
    { key: 'apply_setup_time_dxxfjr', label: '大型消费金融公司' },
    { key: 'apply_setup_time_p2pwd', label: 'P2P平台' },
    { key: 'apply_setup_time_xedkgs', label: '小额贷款公司' },
    { key: 'apply_setup_time_other', label: '其他类型公司' },
  ];
  const demandTableData = demandRowDefs.map((row) => ({
    key: row.key,
    label: row.label,
    d7: vm.demand[`d7_${row.key}`] ?? '-',
    m1: vm.demand[`m1_${row.key}`] ?? '-',
    m3: vm.demand[`m3_${row.key}`] ?? '-',
  }));

  const loanSOther = Math.max(
    0,
    toNumber(vm.loanS.apply_mechanism_number, 0) -
      toNumber(vm.loanS.consumer_apply_mechanism_number, 0) -
      toNumber(vm.loanS.network_loan_apply_mechanis_mnumber, 0),
  );
  const pieData = [
    { type: '消费分期类', value: toNumber(vm.loanS.consumer_apply_mechanism_number, 0) },
    { type: '网络贷款类', value: toNumber(vm.loanS.network_loan_apply_mechanis_mnumber, 0) },
    { type: '其他', value: loanSOther },
  ].filter((d) => (d.value ?? 0) > 0);

  const pieColorMap: Record<string, string> = {
    消费分期类: '#faad14',
    网络贷款类: '#1890ff',
    其他: '#8c8c8c',
    暂无数据: '#d9d9d9',
  };
  const pieConfig: any = {
    data: pieData.length ? pieData : [{ type: '暂无数据', value: 1 }],
    angleField: 'value',
    colorField: 'type',
    color: (d: any) => pieColorMap[String(d?.type)] || '#d9d9d9',
    radius: 0.85,
    innerRadius: 0.6,
    legend: false,
    tooltip: false,
    label: false,
  };

  const applyTimes = [
    { period: '近1个月', value: toNumber(vm.loanS.apply_time1, 0) },
    { period: '近3个月', value: toNumber(vm.loanS.apply_time3, 0) },
    { period: '近6个月', value: toNumber(vm.loanS.apply_time6, 0) },
    { period: '近12个月', value: toNumber(vm.loanS.apply_time12, 0) },
  ];
  const applyTimesMax = Math.max(0, ...applyTimes.map((d) => toNumber(d.value, 0)));
  const columnConfig: any = {
    data: applyTimes,
    xField: 'period',
    yField: 'value',
    appendPadding: [36, 0, 0, 0],
    yAxis: { nice: true, max: applyTimesMax > 0 ? applyTimesMax + Math.max(1, Math.ceil(applyTimesMax * 0.15)) : undefined },
    label: {
      position: 'top',
      offsetY: 50,
      style: { fill: '#262626', opacity: 0.9, fontWeight: 700,dy: -15  },
    },
    xAxis: { label: { autoHide: true, autoRotate: false } },
    meta: { value: { alias: '次数' } },
  };

  const loanHPeriods = ['1', '3', '6', '12', '24'];
  const loanHRows = [
    { key: 'repay_succ', label: '履约次数' },
    { key: 'repay_money', label: '履约金额' },
    { key: 'repay_fail', label: '还款异常次数' },
  ].map((row) => {
    const record: any = { key: row.key, label: row.label };
    loanHPeriods.forEach((p) => {
      record[`m${p}`] = vm.loanH[`${row.key}${p}`] ?? '-';
    });
    return record;
  });

  const rentPeriods = [
    { key: 'd7', label: '近7天' },
    { key: 'm1', label: '近1个月' },
    { key: 'm3', label: '近3个月' },
    { key: 'm6', label: '近6个月' },
    { key: 'm12', label: '近12个月' },
  ];
  const rentRows = [
    { key: 'apply_agency_time', label: '租赁申请机构' },
    { key: 'apply_time', label: '租赁申请次数' },
  ].map((row) => {
    const rec: any = { key: row.key, label: row.label };
    rentPeriods.forEach((p) => {
      rec[p.key] = vm.rentHistory[`${p.key}_${row.key}`] ?? '-';
    });
    return rec;
  });

  const scoreSegments = getScoreSegments();
  const { leftPct, activeIdx, activeColor } = getScorePosition(vm.risk.score);
  const scoreNum = vm.risk.score;
  const advice =
    scoreNum === null
      ? { text: '-', color: '#d9d9d9' }
      : scoreNum > 530
        ? { text: '建议通过', color: '#1890ff' }
        : scoreNum > 430
          ? { text: '建议审核', color: '#faad14' }
          : { text: '建议拒绝', color: '#f5222d' };
  const scoreBarHeight = 28;
  const riskResultView = (
    <ReportSection
      title="风控结果"
      extra={
        <>
          <span>生产时间: {vm.header.createdTime}</span>
          <span>有效期至: {vm.header.validTime}</span>
        </>
      }
      bodyStyle={{ padding: 24 }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: '#262626' }}>风险建议：</span>
        <span style={{ fontSize: 22, fontWeight: 700, color: advice.color }}>{advice.text}</span>
      </div>

      <div style={{ position: 'relative', marginTop: 14 }}>
        {leftPct === null ? null : (
          <div style={{ position: 'absolute', left: `${leftPct}%`, top: -34, transform: 'translateX(-50%)', zIndex: 2 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div
                style={{
                  padding: '2px 10px',
                  borderRadius: 6,
                  background: activeColor,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: '18px',
                  whiteSpace: 'nowrap',
                }}
              >
                {scoreNum}
              </div>
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: `6px solid ${activeColor}`,
                  marginTop: -1,
                }}
              />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', height: scoreBarHeight, borderRadius: 999, overflow: 'hidden' }}>
          {scoreSegments.map((s, i) => (
            <div
              key={s.label}
              style={{
                flex: 1,
                backgroundColor: s.color,
                opacity: activeIdx === i ? 1 : 0.9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {s.label}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: '#bfbfbf' }}>
          <span>650</span>
          <span>570</span>
          <span>530</span>
          <span>470</span>
          <span>430</span>
          <span>0</span>
        </div>
      </div>
    </ReportSection>
  );

  const pieLegendRows = pieData.length ? pieData : [{ type: '暂无数据', value: 0 }];

  const loanFIndicatorRow = {
    key: 'row',
    lenders: renderPlainValue(vm.loanF.lenders),
    cflenders: renderPlainValue(vm.loanF.cflenders),
    nllenders: renderPlainValue(vm.loanF.nllenders),
    lend_time: renderPlainValue(vm.loanF.lend_time),
    lend_time_distance: renderPlainValue(vm.loanF.lend_time_distance),
  };

  const loanFCountRow = {
    key: 'row',
    m1: renderPlainValue(vm.loanF.lend_number1),
    m3: renderPlainValue(vm.loanF.lend_number3),
    m6: renderPlainValue(vm.loanF.lend_number6),
    m12: renderPlainValue(vm.loanF.lend_number12),
    m24: renderPlainValue(vm.loanF.lend_number24),
  };

  return (
    <div style={{ padding: 20 }}>
      <>
        <Typography.Title level={4} style={{ marginBottom: 16 }}>
          {vm.header.reportTitle}
        </Typography.Title>

        {riskResultView}

        <ReportSection title="风险标注">
          <Descriptions bordered size="small" column={1}>
            {vm.riskTagRows && vm.riskTagRows.length > 0 ? (
              vm.riskTagRows.map((r) => (
                <Descriptions.Item key={r.key} label={r.label}>
                  {r.detail}
                </Descriptions.Item>
              ))
            ) : (
              <Descriptions.Item label="命中项">-</Descriptions.Item>
            )}
          </Descriptions>
        </ReportSection>

        <ReportSection title="身份核验">
          <Table
            size="small"
            bordered
            pagination={false}
            columns={[
              {
                title: '运营商核验',
                dataIndex: 'networkTripleElements',
                key: 'networkTripleElements',
                render: (v: any) => identMap[String(v)] || renderPlainValue(v),
              },
              {
                title: '实名核验',
                dataIndex: 'identityTwoElements',
                key: 'identityTwoElements',
                render: (v: any) => identMap[String(v)] || renderPlainValue(v),
              },
              {
                title: '运营商在网时长',
                dataIndex: 'timeOnline',
                key: 'timeOnline',
                render: (v: any) => timeOnlineMap[String(v)] || renderPlainValue(v),
              },
            ]}
            dataSource={[
              {
                key: 'row',
                networkTripleElements: vm.identity.networkTripleElements,
                identityTwoElements: vm.identity.identityTwoElements,
                timeOnline: vm.identity.timeOnline,
              },
            ]}
            rowKey="key"
          />
        </ReportSection>

        <ReportSection title="风险名单">
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {riskListKeysOrdered.map((k, idx) => {
                const hit = isHit(vm.riskListCheck[k]);
                const headBg = hit ? '#ff4d4f' : '#52c41a';
                const textColor = hit ? '#ff4d4f' : '#52c41a';
                const isLastCol = (idx + 1) % 4 === 0;
                const isLastRow = idx >= riskListKeysOrdered.length - 4;
                return (
                  <div
                    key={k}
                    style={{
                      borderRight: isLastCol ? 'none' : '1px solid #f0f0f0',
                      borderBottom: isLastRow ? 'none' : '1px solid #f0f0f0',
                    }}
                  >
                    <div
                      style={{
                        background: headBg,
                        color: '#fff',
                        textAlign: 'center',
                        padding: '10px 12px',
                        fontWeight: 700,
                        fontSize: 13,
                        lineHeight: '18px',
                      }}
                    >
                      {riskListLabelMap[k] || k}
                    </div>
                    <div style={{ background: '#fff', textAlign: 'center', padding: '12px 12px', fontWeight: 700, color: textColor }}>
                      {hit ? '命中' : '未命中'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </ReportSection>

        <ReportSection title="查询次数">
          <Table
            size="small"
            bordered
            pagination={false}
            dataSource={demandTableData}
            tableLayout="fixed"
            columns={[
              { title: '查询类型', dataIndex: 'label', key: 'label', width: 180 },
              { title: '近7天', dataIndex: 'd7', key: 'd7', width: 180, align: 'center', render: (v: any) => renderPlainValue(v) },
              { title: '近30天', dataIndex: 'm1', key: 'm1', width: 180, align: 'center', render: (v: any) => renderPlainValue(v) },
              { title: '近90天', dataIndex: 'm3', key: 'm3', width: 180, align: 'center', render: (v: any) => renderPlainValue(v) },
            ]}
            rowKey="key"
          />
        </ReportSection>

        <ReportSection title="历史借贷行为">
          <ReportSubTitle style={{ marginBottom: 12 }}>近12个月网贷申请</ReportSubTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 24 }}>
            <div>
              <div style={{ height: 260, marginTop: 8 }}>
                <Pie {...pieConfig} />
              </div>
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {pieLegendRows.map((d) => (
                  <div key={String(d.type)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 99, background: pieColorMap[String(d.type)] || '#d9d9d9' }} />
                    <span style={{ color: '#595959' }}>{d.type}</span>
                    <span style={{ color: '#262626', fontWeight: 700 }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div style={{ height: 260, marginTop: 8 }}>
                <Column {...columnConfig} />
              </div>
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <Descriptions column={3} bordered size="small">
              <Descriptions.Item label="申请机构总数">{renderPlainValue(vm.loanS.apply_mechanism_number)}</Descriptions.Item>
              <Descriptions.Item label="最近一次申请时间">{renderPlainValue(vm.loanS.last_apply_time)}</Descriptions.Item>
              <Descriptions.Item label="距离最近一次申请已有（天）">{renderPlainValue(vm.loanS.last_apply_time_distance)}</Descriptions.Item>
            </Descriptions>
          </div>

          <div style={{ marginTop: 18 }}>
            <ReportSubTitle style={{ marginBottom: 12 }}>近24个月网贷放款</ReportSubTitle>
            <Table
              size="small"
              bordered
              pagination={false}
              tableLayout="fixed"
              dataSource={[loanFIndicatorRow]}
              columns={[
                { title: '放款总机构数', dataIndex: 'lenders', key: 'lenders', width: 140, align: 'center' },
                { title: '分期类放款机构数', dataIndex: 'cflenders', key: 'cflenders', width: 160, align: 'center' },
                { title: '网络贷款类放款机构数', dataIndex: 'nllenders', key: 'nllenders', width: 170, align: 'center' },
                { title: '最近一次放款日期', dataIndex: 'lend_time', key: 'lend_time', width: 160, align: 'center' },
                { title: '距离最近一次放款已有（天）', dataIndex: 'lend_time_distance', key: 'lend_time_distance', width: 190, align: 'center' },
              ]}
              rowKey="key"
            />

            <Table
              size="small"
              bordered
              pagination={false}
              style={{ marginTop: 14 }}
              tableLayout="fixed"
              dataSource={[loanFCountRow]}
              columns={[
                { title: '近1个月', dataIndex: 'm1', key: 'm1', width: 120, align: 'center' },
                { title: '近3个月', dataIndex: 'm3', key: 'm3', width: 120, align: 'center' },
                { title: '近6个月', dataIndex: 'm6', key: 'm6', width: 120, align: 'center' },
                { title: '近12个月', dataIndex: 'm12', key: 'm12', width: 120, align: 'center' },
                { title: '近24个月', dataIndex: 'm24', key: 'm24', width: 120, align: 'center' },
              ]}
              rowKey="key"
            />
          </div>

          <div style={{ marginTop: 18 }}>
            <ReportSubTitle style={{ marginBottom: 12 }}>近24个月网贷履约</ReportSubTitle>
          <Table
            size="small"
            bordered
            pagination={false}
            tableLayout="fixed"
            dataSource={loanHRows}
            columns={[
              { title: '指标', dataIndex: 'label', key: 'label', width: 140, ellipsis: true },
              { title: '近1个月', dataIndex: 'm1', key: 'm1', width: 120, align: 'center', render: (v: any) => renderPlainValue(v) },
              { title: '近3个月', dataIndex: 'm3', key: 'm3', width: 120, align: 'center', render: (v: any) => renderPlainValue(v) },
              { title: '近6个月', dataIndex: 'm6', key: 'm6', width: 120, align: 'center', render: (v: any) => renderPlainValue(v) },
              { title: '近12个月', dataIndex: 'm12', key: 'm12', width: 120, align: 'center', render: (v: any) => renderPlainValue(v) },
              { title: '近24个月', dataIndex: 'm24', key: 'm24', width: 120, align: 'center', render: (v: any) => renderPlainValue(v) },
            ]}
            rowKey="key"
          />
          </div>
        </ReportSection>

        <ReportSection title="历史逾期记录">
          <Table
            size="small"
            bordered
            pagination={false}
            tableLayout="fixed"
            dataSource={[
              {
                key: 'row',
                overdue_mechanism_number: vm.overdueHistory.overdue_mechanism_number,
                counts: vm.overdueHistory.counts,
                overdue_money: vm.overdueHistory.overdue_money,
              },
            ]}
            columns={[
              {
                title: '逾期机构总数',
                dataIndex: 'overdue_mechanism_number',
                key: 'overdue_mechanism_number',
                align: 'center',
                render: (v: any) => renderPlainValue(v),
              },
              { title: '逾期总次数', dataIndex: 'counts', key: 'counts', align: 'center', render: (v: any) => renderPlainValue(v) },
              { title: '逾期总金额（元）', dataIndex: 'overdue_money', key: 'overdue_money', align: 'center', render: (v: any) => renderPlainValue(v) },
            ]}
            rowKey="key"
          />
        </ReportSection>

        <ReportSection title="租赁行为">
          <Table
            size="small"
            bordered
            pagination={false}
            tableLayout="fixed"
            dataSource={rentRows}
            columns={[
              { title: '指标', dataIndex: 'label', key: 'label', width: 140, ellipsis: true },
              { title: '近7天', dataIndex: 'd7', key: 'd7', width: 120, align: 'center', render: (v: any) => renderPlainValue(v) },
              { title: '近1个月', dataIndex: 'm1', key: 'm1', width: 120, align: 'center', render: (v: any) => renderPlainValue(v) },
              { title: '近3个月', dataIndex: 'm3', key: 'm3', width: 120, align: 'center', render: (v: any) => renderPlainValue(v) },
              { title: '近6个月', dataIndex: 'm6', key: 'm6', width: 120, align: 'center', render: (v: any) => renderPlainValue(v) },
              { title: '近12个月', dataIndex: 'm12', key: 'm12', width: 120, align: 'center', render: (v: any) => renderPlainValue(v) },
            ]}
            rowKey="key"
          />
        </ReportSection>

        <ReportSection title="关联风险监测">
          <Table
            size="small"
            bordered
            pagination={false}
            columns={[
              {
                title: '近3个月身份证关联手机号数',
                dataIndex: 'm3_idcard_to_phone_time',
                key: 'm3_idcard_to_phone_time',
                align: 'center',
                render: (v: any) => renderPlainValue(v),
              },
              {
                title: '近3个月手机号关联身份证数',
                dataIndex: 'm3_phone_to_idcard_time',
                key: 'm3_phone_to_idcard_time',
                align: 'center',
                render: (v: any) => renderPlainValue(v),
              },
            ]}
            dataSource={[
              {
                key: 'row',
                m3_idcard_to_phone_time: vm.relevanceRisk.m3_idcard_to_phone_time,
                m3_phone_to_idcard_time: vm.relevanceRisk.m3_phone_to_idcard_time,
              },
            ]}
            rowKey="key"
          />
        </ReportSection>

        <ReportSection title="法院风险信息" style={{ marginBottom: 0 }}>
          <Table
            size="small"
            bordered
            dataSource={(vm.courtRiskList || []).map((r: any, i: number) => ({ ...r, __idx: i }))}
            columns={[
              { title: '序号', dataIndex: '__idx', key: '__idx', width: 80, render: (_: any, __: any, i: number) => i + 1 },
              { title: '审结日期', dataIndex: 'sort_time_string', key: 'sort_time_string', render: (v: any) => renderPlainValue(v) },
              { title: '类型', dataIndex: 'data_type', key: 'data_type', render: (v: any) => dataTypeMap[String(v)] || renderPlainValue(v) },
              { title: '摘要说明', dataIndex: 'summary', key: 'summary', render: (v: any) => renderPlainValue(v) },
              {
                title: '文书详情',
                dataIndex: 'detail',
                key: 'detail',
                render: (_: any, r: any) =>
                  renderPlainValue(
                    r?.detail ??
                      r?.doc_detail ??
                      r?.document_detail ??
                      r?.wenshu_detail ??
                      r?.case_no ??
                      r?.caseNo ??
                      r?.url ??
                      r?.detail_url ??
                      '',
                  ),
              },
              { title: '匹配度', dataIndex: 'compatibility', key: 'compatibility', width: 120, render: (v: any) => formatPercent(v) },
            ]}
            pagination={{ pageSize: 8, showSizeChanger: false }}
            rowKey={(r: any) => r.__idx}
            locale={{ emptyText: '-' }}
          />
          <div style={{ marginTop: 12, fontSize: 12 }}>
            <div style={{ color: '#f5222d' }}>注：匹配度低于80%，不一定是本人，需要进一步确认</div>
            <div style={{ marginTop: 4 }}>
              信息查询：
              <a
                href="https://wenshu.court.gov.cn/website/wenshu/181029CR4M5A62CH/index.html?"
                target="_blank"
                rel="noreferrer"
                style={{ marginLeft: 8 }}
              >
                中国裁判文书网
              </a>
              <span style={{ margin: '0 6px' }}>|</span>
              <a href="https://zxgk.court.gov.cn/" target="_blank" rel="noreferrer">
                中国执行信息公开网
              </a>
            </div>
          </div>
        </ReportSection>
      </>
    </div>
  );
};

export default BuerXingyaoTab;
